import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Http2Service } from '@services/http2.service';
import { map, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, of } from 'rxjs';

export interface Notification {
  _id: string;
  message_id: string;
  data: {
    program_id: string;
  };
  from: string;
  notification: {
    title: string;
    body: string;
  };
  sentTime: string;
  status: 'read' | 'unread';
}

export interface PaginatedNotificationsResponse {
  data: Notification[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface PaginatedNotificationsRequest {
  language: string;
  page: number;
  page_size: number;
  isRead?: boolean;
  user_uid?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  public baseUrl: string;
  public onChange$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  constructor(private http: Http2Service) {
    this.baseUrl = environment.baseCpuUrl;
  }

  getPhysioNotificationAboutPatient(patientId: string, language?: string) {
    language = (language || 'en').toLocaleLowerCase();
    let requestData: any = {
      patient_uid: patientId,
      language: language,
    };

    return this.http
      .fetch(`${this.baseUrl}v2/get_notifications_by_patient`, requestData)
      .pipe(
        map((response) => {
          return response.data;
        }),
      )
      .pipe(
        map((list) => {
          return list.map((n) => ({
            ...n,
            send_time: DateTime.fromISO(n.send_time, { zone: 'utc' }).toLocal(),
          }));
        }),
      );
  }
  checkUnreadPatientsNotifications(patientsIds: string[]) {
    let requestData: any = {
      patient_list: patientsIds,
    };
    return this.http
      .fetch(`${this.baseUrl}check_unread_patient_notification`, requestData)
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  getNotifications(lang: string, isRead?: boolean) {
    lang = lang || 'en';
    const requestData: any = { language: lang.toLowerCase() };
    if (isRead !== undefined) {
      requestData['isRead'] = isRead;
    }

    return this.http
      .fetch(`${this.baseUrl}v2/get_notifications`, requestData)
      .pipe(
        map((response) => {
          return response.data;
        }),
      )
      .pipe(
        map((list) => {
          return list
            ? list.map((n) => ({
                ...n,
                send_time: DateTime.fromISO(n.send_time, {
                  zone: 'utc',
                }).toLocal(),
              }))
            : of([]);
        }),
      );
  }

  getNotificationsPage(
    request: PaginatedNotificationsRequest,
  ): Observable<PaginatedNotificationsResponse> {
    const requestData: PaginatedNotificationsRequest = {
      language: (request.language || 'en').toLowerCase(),
      page: request.page,
      page_size: request.page_size,
    };

    if (request.isRead !== undefined) {
      requestData.isRead = request.isRead;
    }
    if (request.user_uid) {
      requestData.user_uid = request.user_uid;
    }

    return this.http
      .fetch(`${this.baseUrl}v2/get_notifications`, requestData)
      .pipe(
        map((response) => {
          const notifications = Array.isArray(response?.data)
            ? response.data.map((notification) => ({
                ...notification,
                send_time: DateTime.fromISO(notification.send_time, {
                  zone: 'utc',
                }).toLocal(),
              }))
            : [];

          return {
            data: notifications,
            total_count:
              typeof response?.total_count === 'number'
                ? response.total_count
                : notifications.length,
            page:
              typeof response?.page === 'number' ? response.page : request.page,
            page_size:
              typeof response?.page_size === 'number'
                ? response.page_size
                : request.page_size,
          };
        }),
      );
  }

  markAllAsRead(id_token: string) {
    const requestData = {
      id_token: id_token,
    };

    return this.http.fetch(this.baseUrl + 'mark_all_as_read', requestData).pipe(
      tap((a) => {
        this.onChange$.next(Date.now());
      }),
    );
  }

  markAsReadByPatient(patient_uid: string) {
    const requestData = {
      patient_uid,
    };

    return this.http
      .fetch(this.baseUrl + 'mark_as_read_by_patient', requestData)
      .pipe(
        tap((a) => {
          this.onChange$.next(Date.now());
        }),
      );
  }

  createNotification(
    id_token: string,
    sender: string,
    user_id: string,
    type: string,
    message: {},
    data: {},
  ) {
    const requestData = {
      id_token: id_token,
      sender: sender,
      user_id: user_id,
      type: type,
      message: message || {},
      data: data || {},
    };

    return this.http.fetch(this.baseUrl + 'create_notification', requestData);
  }

  deleteNotification(id_token, notificationId: string) {
    const requestData = {
      id_token: id_token,
      notificationId: notificationId,
    };

    return this.http.fetch(this.baseUrl + 'delete_notification', requestData);
  }
}
